import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, CalendarDays } from 'lucide-react';
import { Button } from '../ui/button';
import { HeroCommonExtras, AnimatedHeadlineWords, cmsUrl } from './HeroBase';
import { ConfettiCanvas } from '../ConfettiCanvas';

// Cinematic minimalist hero — full-bleed photo + tight typography.
export const HeroMinimalist = ({ cms = {}, onPrimary, onOpenAvailability }) => {
  const v = cms.variants?.minimalist || {};
  const centered = v.text_align === 'center';
  const primaryCta = cms.primary_cta_text   || 'Build Your Dream Party';
  const secondCta  = cms.secondary_cta_text || 'Check Availability';
  const confettiOn = cms.confetti_enabled !== false;
  const extras = HeroCommonExtras({ cms, align: centered ? 'center' : 'left', onDark: true });

  return (
    <section
      className="relative min-h-[80vh] flex items-end sm:items-center overflow-hidden bg-[#1E1E24]"
      data-testid="landing-hero"
      data-variant="minimalist"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: v.background_image_url
            ? `url(${cmsUrl(v.background_image_url)})`
            : 'linear-gradient(135deg, #1E1E24 0%, #FF3B6B 100%)',
        }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1E1E24]/85 via-[#1E1E24]/35 to-transparent" aria-hidden />
      {confettiOn && <ConfettiCanvas count={100} />}

      <motion.div
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}
        className={`relative z-10 container mx-auto px-6 lg:px-12 pt-24 pb-16 sm:py-32 ${centered ? 'text-center max-w-3xl' : 'max-w-2xl'}`}
      >
        <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.25em] text-white/85 mb-6" data-testid="hero-badge">
          {cms.badge_text || 'Creative Birthdays'}
        </span>
        <AnimatedHeadlineWords
          cms={cms}
          onDark
          align={centered ? 'center' : 'left'}
          className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] mb-6"
          showEmoji={false}
        />
        {extras.sub}
        <div className={`${centered ? 'flex justify-center' : ''}`}>{extras.location}</div>
        <div className={`${centered ? 'flex justify-center' : ''}`}>{extras.pills({ when: 'above' })}</div>
        <div className={`flex flex-wrap gap-3 mt-4 ${centered ? 'justify-center' : ''}`}>
          <Button onClick={onPrimary} size="lg" className="rounded-none bg-white hover:bg-white/90 text-[#1E1E24] px-8 font-medium" data-testid="hero-primary-cta">
            <Calendar className="w-4 h-4 mr-2" /> {primaryCta}
          </Button>
          {!v.single_cta_only && (
            <Button onClick={onOpenAvailability} variant="outline" size="lg" className="rounded-none border-white/60 bg-transparent hover:bg-white/10 text-white px-8 font-medium" data-testid="hero-secondary-cta">
              <CalendarDays className="w-4 h-4 mr-2" /> {secondCta}
            </Button>
          )}
        </div>
        <div className={`${centered ? 'flex justify-center' : ''}`}>{extras.pills({ when: 'below' })}</div>
        {extras.trustBadges}
      </motion.div>
    </section>
  );
};

export default HeroMinimalist;
